import React from 'react';

import constants from 'constants/constants';
import moment from 'moment-timezone';
import { isStringHasRealContent } from 'utilitys/helpers/general';

import { useAssigneeOptionsList } from 'hooks/useAssigneeOptionsList';

import { CloseButton } from 'components/common/CloseButton/CloseButton';
import Icon from 'components/common/Icons/base/Icon/Icon';
import { MuiIcon } from 'components/common/Icons/base/MuiIcon/MuiIcon';
import OverlayWithSpinner from 'components/common/OverlayWithSpinner/overlayWithSpinner';
import { ProfileImage } from 'components/common/ProfileImage/profileImage';
import RichTextArea from 'components/common/richTextArea/richTextArea';

import { userMentionMapper } from 'components/Dashboards/Program/Dashboard/ProgramDashboardComponents/Battlecards/MainCard/MainCard.utils';
import { Button } from 'components/DesignSystem/Button';
import { CustomizedConfirmDeleteModal } from 'components/common';
import classes from 'components/pages/ReportPage/ReportPageHeader/reportComments/ReportComments.module.scss';
import { useReportCommentProvider } from 'components/pages/ReportPage/ReportPageHeader/reportComments/useReportCommentProvider';

interface Props {
    reportId: number;
    setDisplayCommentsDrawer: (value: boolean) => void;
    queryKeys?: string[];
}

export const ReportComments = ({ reportId, setDisplayCommentsDrawer, queryKeys = [] }: Props) => {
    const { options: assigneeOptions } = useAssigneeOptionsList(false);
    const users = assigneeOptions.map((item) => userMentionMapper(item.assignee));

    const {
        cancelAddCommentMode,
        commentData,
        commentsEditorStatus,
        confirmDeleteComment,
        createComment,
        deleteCommentModalShow,
        email,
        firstName,
        lastName,
        handleCommentDelete,
        handleEditorDescription,
        image,
        setAddCommentMode,
        setCommentData,
        setDeleteCommentModalShow,
        sortedComments,
        updateComment,
        username,
        showLoader,
        setCommentsEditorStatus,
    } = useReportCommentProvider({ reportId: reportId, queryKeys });

    return (
        <div className={classes.commentsContainer} data-track-id={`comments-card`}>
            <div className={classes.titleWrapper}>
                <div className={classes.title}>{`Comments (${sortedComments.length})`}</div>
                <div className={classes.buttonsAndCloseIconWrapper}>
                    {commentsEditorStatus.isOpen && (
                        <div className={classes.actionsWrapper}>
                            <Button variant={'secondary'} onClick={cancelAddCommentMode}>
                                Cancel
                            </Button>
                            <Button variant={'primary'} onClick={createComment}>
                                Add
                            </Button>
                        </div>
                    )}
                    <CloseButton onClick={() => setDisplayCommentsDrawer(false)} />
                </div>
            </div>
            <div className={classes.addCommentContainer}>
                <div className={classes.profileImageWrapper}>
                    <ProfileImage fullName={`${firstName} ${lastName}`} email={email} imageName={image || ''} />
                </div>
                {!commentsEditorStatus.isOpen ? (
                    <div className={classes.addCommentAndQuickCommentsContainer}>
                        <button
                            data-testid={'add-comment'}
                            className={classes.addCommentInput}
                            onClick={() => setAddCommentMode('')}
                        >
                            Add a comment...
                        </button>
                    </div>
                ) : (
                    <div className={classes.addCommentWrapper}>
                        <div data-testid={'comments-text-area'} className={classes.textArea}>
                            <RichTextArea
                                onEditorDescription={handleEditorDescription}
                                existingData={commentData.commentMessage}
                                editorContentHeight={180}
                                isAutoFocus={true}
                                isError={commentData.error}
                                mention={{
                                    separator: ' ',
                                    trigger: '@',
                                    suggestions: users,
                                }}
                                placeholder={
                                    'Enter your comments here. \nType @ to mention another user and notify them about it.'
                                }
                            />
                        </div>
                    </div>
                )}
            </div>

            <div className={classes.separator} />

            {showLoader ? (
                <OverlayWithSpinner />
            ) : (
                <div className={classes.commentsWrapper}>
                    {sortedComments.map((item) => (
                        <div key={item.updatedDate} className={classes.commentWrapper}>
                            <ProfileImage
                                fullName={`${item.firstName} ${item.lastName}`}
                                email={item.userName}
                                imageName={item.imageName || ''}
                            />
                            <div data-testid={'comments-row'} className={classes.wrapper}>
                                <div className={classes.iconsWrapper}>
                                    <div className={classes.iconsHeader}>
                                        <span>{`${item.firstName} ${item.lastName}`}</span>
                                        <span>
                                            {moment(item.updatedDate).format(
                                                constants.formats.date.fullDateWithMilliSeconds,
                                            )}
                                        </span>

                                        {username === item.userName && (
                                            <div className={classes.actionIcons}>
                                                <Button
                                                    variant={'tertiary'}
                                                    onClick={() => {
                                                        setCommentData({
                                                            ...commentData,
                                                            id: item.commentId,
                                                            commentMessage: '',
                                                            commentIdInEditMode: item.commentId,
                                                            error: false,
                                                        });
                                                        setCommentsEditorStatus({
                                                            isOpen: false,
                                                            isError: false,
                                                        });
                                                    }}
                                                >
                                                    <Icon
                                                        name={'edit-user-profile'}
                                                        size={'16px'}
                                                        color={'var(--button-tertiary-fg)'}
                                                    />
                                                </Button>
                                                <Button
                                                    variant={'tertiary'}
                                                    onClick={() => {
                                                        confirmDeleteComment(item.commentId);
                                                    }}
                                                >
                                                    <MuiIcon
                                                        data-testid={'delete-icon'}
                                                        icon={'delete'}
                                                        type={'outlined'}
                                                        fontSize={'16px'}
                                                        color={'var(--button-tertiary-fg)'}
                                                    />
                                                </Button>
                                            </div>
                                        )}
                                    </div>

                                    {commentData.commentIdInEditMode === item.commentId && (
                                        <div className={classes.actionsWrapper}>
                                            <Button
                                                variant={'secondary'}
                                                onClick={() => {
                                                    setCommentData({
                                                        ...commentData,
                                                        id: null,
                                                        commentMessage: '',
                                                        commentIdInEditMode: null,
                                                    });
                                                }}
                                            >
                                                Cancel
                                            </Button>
                                            <Button
                                                variant={'primary'}
                                                onClick={() => {
                                                    updateComment(item.commentId);
                                                }}
                                            >
                                                Update
                                            </Button>
                                        </div>
                                    )}
                                </div>

                                <div data-testid={'comment-text'} className={classes.commentWrapper}>
                                    {commentData.commentIdInEditMode === item.commentId ? (
                                        <div className={`${classes.textArea} ${classes.comment}`}>
                                            <RichTextArea
                                                onEditorDescription={handleEditorDescription}
                                                existingData={
                                                    commentData.id === item.commentId &&
                                                    !isStringHasRealContent(commentData.commentMessage)
                                                        ? commentData.commentMessage
                                                        : item.comment
                                                }
                                                editorContentHeight={160}
                                                isAutoFocus={true}
                                                isError={commentData.error}
                                                mention={{
                                                    separator: ' ',
                                                    trigger: '@',
                                                    suggestions: users,
                                                }}
                                            />
                                        </div>
                                    ) : (
                                        <span
                                            className={classes.comment}
                                            dangerouslySetInnerHTML={{ __html: item.comment }}
                                        />
                                    )}
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {commentData.commentIdToRemove && (
                <CustomizedConfirmDeleteModal
                    isShowModal={deleteCommentModalShow}
                    hideModal={() => {
                        setDeleteCommentModalShow(false);
                    }}
                    text={'Are you sure you want to delete this Comment?'}
                    onConfirm={() => handleCommentDelete(commentData.commentIdToRemove as number)}
                />
            )}
        </div>
    );
};
